
import './HeroSection.css';
import { useState, useEffect } from 'react';
import Icon from '../../../components/ui/AppIcon';
import AppImage from '../../../components/ui/AppImage';
import Enquiry_Form_Modal from '../../enquiry_form_modal/Enquiry_Form_Modal';
import FloatingCareerSupport from './floating_career_support/FloatingCareerSupport';
import Floating_Call_Support from './floating_call_support/Floating_Call_Support';
import Offer_Modal from '../../offer_modal/Offer_Modal';

interface HeroSectionProps {
  onApplyClick: () => void;
  onFeeDetailsClick: () => void;
}

const HeroSection = ({ onApplyClick, onFeeDetailsClick }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [enrolledCount, setEnrolledCount] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const targetCount = 110000;
    const duration = 2000;
    const increment = targetCount / (duration / 16);
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= targetCount) {
        setEnrolledCount(targetCount);
        clearInterval(timer);
      } else {
        setEnrolledCount(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [isHydrated]);

  const [showStickyModal, setShowStickyModal] = useState(false);

  return (
    <section className="relative min-h-[80vh] flex items-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 overflow-hidden HeroSection">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, rgb(30, 64, 175) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="max-w-content mx-auto px-section-margin py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Trust Badge */}
            <div className="inline-flex items-center space-x-2 bg-success/10 border border-success/20 rounded-full px-4 py-2">
              <Icon name="CheckBadgeIcon" size={20} className="text-success" variant="solid" />
              <span className="text-sm font-semibold text-success ugc_text">
                UGC Approved Online MBA
              </span>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary leading-tight title_hero_text">
                Advance Your Career with a UGC-Approved Online MBA from a{' '}
                <span className="text-primary">Top Indian University</span>
              </h1>
              <p className="text-lg md:text-xl text-text-secondary leading-relaxed">
                Earn a Recognized MBA While You Work — 100% Online, Flexible Schedule,
                Industry-Driven Learning.
              </p>
            </div>

            {/* Key Benefits */}
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: 'ClockIcon', text: '100% Online & Flexible' },
                { icon: 'CurrencyRupeeIcon', text: 'Affordable EMI Options' },
                { icon: 'AcademicCapIcon', text: '100% Job Placement Assistant' },
                { icon: 'BriefcaseIcon', text: 'Industry Focused Learning' },
              ].map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 bg-background/80 backdrop-blur-sm rounded-lg p-4 border border-border"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon
                      name={benefit.icon as any}
                      size={25}
                      className="text-primary"
                      variant="outline"
                    />
                  </div>
                  <span className="text-sm font-medium text-text-primary inner_benifits_text">
                    {benefit.text}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => {
                  setShowStickyModal(true);
                }}
                className="flex-1 sm:flex-initial bg-accent text-accent-foreground px-8 py-4 rounded-lg font-bold text-lg shadow-cta hover:bg-accent/90 transition-default flex items-center justify-center space-x-2"
                style={{ minHeight: '56px' }}
              >
                <Icon name="AcademicCapIcon" size={24} variant="solid" />
                <span>Apply Now</span>
              </button>
              <button
                // onClick={onFeeDetailsClick}
                onClick={() => {
                  setShowStickyModal(true);
                }}
                className="flex-1 sm:flex-initial bg-background text-primary border-2 border-primary px-8 py-4 rounded-lg font-bold text-lg hover:bg-primary/5 transition-default flex items-center justify-center space-x-2"
                style={{ minHeight: '56px' }}
              >
                <Icon name="ArrowDownTrayIcon" size={24} variant="outline" />
                <span>Download Brochure</span>
              </button>
            </div>

            {/* Trust Indicators */}
            {/* <div className="flex flex-wrap items-center gap-6 pt-4 border-t border-border">
              <div className="flex items-center space-x-2">
                <Icon name="StarIcon" size={20} className="text-warning" variant="solid" />
                <span className="text-sm text-text-secondary">4.8/5 Student Rating</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="UserGroupIcon" size={20} className="text-primary" variant="solid" />
                <span className="text-sm text-text-secondary">15,000+ Alumni Network</span>
              </div>
            </div> */}
          </div>

          {/* Right Image */}
          <div className="relative right_img_holder">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_171170bd3-1764687877901.png"
                alt="Diverse group of working professionals collaborating in modern office with laptops and documents"
                className="w-full h-auto"
              />

              {/* Floating Stats Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-background/95 backdrop-blur-md rounded-xl p-3 shadow-xl border border-border">
                <div className="grid grid-cols-3 gap-1">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white count_text">100%</div>
                    <div className="text-xs text-white mt-1 count_bottom_text">Online</div>
                  </div>
                  <div className="text-center border-x border-border">
                    <div className="text-2xl font-bold text-white count_text">₹1.89L</div>
                    <div className="text-xs text-white mt-1 count_bottom_text">Total Fees</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white count_text">24 Months</div>
                    <div className="text-xs text-white mt-1 count_bottom_text">Duration</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-accent/10 rounded-full blur-3xl" />
          </div>
        </div>

        {/* <FloatingCareerSupport/> */}
        <Floating_Call_Support />
      </div>

      <Enquiry_Form_Modal
        show={showStickyModal}
        onHide={() => {
          setShowStickyModal(false);
        }}
      />

      <Offer_Modal />
    </section>
  );
};

export default HeroSection;
